/**
 * API Client Configuration
 * 
 * Centralized Axios instance for all API requests.
 * 
 * Features:
 * - HttpOnly cookie authentication (automatic)
 * - Automatic 401 handling with redirect to login
 * - Consistent headers and base URL
 * 
 * @example
 * import { apiClient } from '@/services/api/client';
 * const response = await apiClient.get('/users');
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import logger from '../../utils/logger';
import { env } from '../../config/runtimeEnv';

// =============================================================================
// CONFIGURATION
// =============================================================================

/** API base URL from runtime env (window.__ENV__) or build-time env, falling back to localhost */
const API_BASE_URL = (env.API_URL || env.VITE_API_BASE_URL || 'http://localhost:3117')
    .replace(/\/api\/v1\/?$/, '')
    .replace(/\/$/, '');

/** Full API URL with version prefix */
const API_URL = `${API_BASE_URL}/api/v1`;

/** Request timeout in milliseconds */
const REQUEST_TIMEOUT = 30000; // 30 seconds

/** Is development mode */
const IS_DEV = process.env.NODE_ENV === 'development';

/** Routes that represent app-scoped contexts and should send X-App-Id */
const APP_ROUTE_PREFIXES = [
    '/hub-management',
    '/haat-requests',
    '/taskify-management',
    '/campaigns-management',
    '/devices-management',
    '/careers-management',
    '/partner-msg-management',
    '/product-team',
    '/qlik-access-management',
    '/haat-screens',
    '/courier-connect-management',
    '/global-management',
    '/market-research-management',
    '/onboarding-scheduler',
    '/targets',
    '/supplier-payments',
] as const;

// Log configuration on startup (dev only)
if (IS_DEV) {
    console.log('[API Client] Initialized with:', { API_BASE_URL, API_URL, IS_DEV });
}

// =============================================================================
// PUBLIC IP DETECTION (DEV ONLY)
// =============================================================================

/** Cached public IP for development testing */
let cachedPublicIp: string | null = null;

/**
 * Fetch the client's public IP address (for development testing only)
 * Uses ipify.org API to get the real public IP
 */
async function fetchPublicIp(): Promise<string | null> {
    if (cachedPublicIp) return cachedPublicIp;

    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        cachedPublicIp = data.ip;
        logger.info(`📍 Detected public IP: ${cachedPublicIp}`);
        return cachedPublicIp;
    } catch (error) {
        logger.error('Failed to fetch public IP');
        return null;
    }
}

// Fetch public IP on startup (dev only)
if (IS_DEV) {
    fetchPublicIp();
}

// =============================================================================
// API CLIENT INSTANCE
// =============================================================================

/**
 * Configured Axios instance for API requests
 * 
 * @remarks
 * - withCredentials: true - Sends HttpOnly cookies automatically
 * - All requests go through interceptors for logging and error handling
 */
const apiClient: AxiosInstance = axios.create({
    baseURL: API_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: true, // Required for HttpOnly cookie auth
});

// =============================================================================
// REQUEST INTERCEPTOR
// =============================================================================

/**
 * Request interceptor for logging and request modification
 */
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Log request in development
        const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
        logger.info(`API Request: ${config.method?.toUpperCase()} ${fullUrl}`);
        if (IS_DEV) {
            console.log(`🔵 [API Client] ${config.method?.toUpperCase()} ${fullUrl}`, {
                baseURL: config.baseURL,
                url: config.url,
                headers: config.headers,
            });
        }

        // DEV ONLY: Add public IP header for testing IP blocking locally
        if (IS_DEV && cachedPublicIp) {
            config.headers['X-Test-Client-IP'] = cachedPublicIp;
        }

        // When in Hub Management, send header so backend runs hub IP allow-list check
        if (typeof window !== 'undefined' && window.location?.pathname?.startsWith('/hub-management')) {
            (config.headers as Record<string, string>)['X-Request-From-Hub'] = 'true';
        }

        // Attach app ID header so backend can associate audit logs with the correct app
        if (typeof window !== 'undefined') {
            const appId = sessionStorage.getItem('appId');
            const pathname = window.location?.pathname || '';
            const isAppRoute = APP_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
            if (appId && isAppRoute) {
                (config.headers as Record<string, string>)['X-App-Id'] = appId;
            }
        }

        return config;
    },
    (error: AxiosError) => {
        logger.error('API Request Error');
        return Promise.reject(error);
    }
);

// =============================================================================
// RESPONSE INTERCEPTOR
// =============================================================================

/** Flag to track if we're showing the blocked page (prevent multiple triggers) */
let isShowingBlockedPage = false;

/**
 * Response interceptor for error handling
 * 
 * Handles:
 * - 403 IP Blocked: Show blank page (HIGHEST PRIORITY)
 * - 401 Unauthorized: Redirect to login
 * - 403 Forbidden (deactivated): Redirect to login
 * - Other errors: Log and propagate
 */
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error: AxiosError<{ message?: string; code?: string; error?: string }>) => {
        const status = error.response?.status;
        const message = error.response?.data?.message || '';
        const errorCode = error.response?.data?.code;
        const errorType = error.response?.data?.error || '';
        const isOnLoginPage = window.location.pathname.includes('/login');
        // Public, login-free mini-app forms (/forms/*) must never be redirected
        // to login: AuthProvider's /auth/me check returns 401 for anonymous
        // visitors, which would otherwise bounce them off the public form.
        const isPublicFormPage = window.location.pathname.startsWith('/forms/');
        // =========================================================================
        // HIGHEST PRIORITY: Handle IP Blocked (403 with IP block indicators)
        // Show blank page to hide the fact that the app exists
        // =========================================================================
        const ipBlockedHeader = error.response?.headers?.['x-ip-blocked'];
        const isIpBlocked403 = (
            status === 403 &&
            (ipBlockedHeader === 'true' ||
                errorType.startsWith('IP_') ||
                errorType === 'IP_BLOCKED' ||
                errorType === 'IP_GLOBALLY_BLOCKED' ||
                errorType === 'IP_NOT_ALLOWED' ||
                errorType === 'IP_BLOCKED_FOR_APP')
        );

        if (isIpBlocked403 && !isShowingBlockedPage) {
            isShowingBlockedPage = true;
            logger.info('🚫 IP Blocked - showing blank page');

            // Replace the entire page with a blank white page
            document.body.innerHTML = '';
            document.body.style.cssText = `
                margin: 0;
                padding: 0;
                background: white;
                min-height: 100vh;
            `;
            document.title = '';

            // Prevent any further navigation
            window.history.pushState(null, '', window.location.href);
            window.onpopstate = () => {
                window.history.pushState(null, '', window.location.href);
            };

            // Return a never-resolving promise to stop all further processing
            return new Promise(() => { });
        }

        // Handle 401 Unauthorized - session expired, app access revoked, or IP blocked
        if (status === 401 && !isOnLoginPage && !isPublicFormPage) {
            const isIpBlocked =
                errorCode === 'IP_BLOCKED' ||
                error.response?.headers?.['x-ip-blocked'] === 'true';
            if (isIpBlocked) {
                logger.info('IP blocked, redirecting to login');
                window.location.href = '/login?reason=ip_blocked';
            } else if (errorCode === 'APP_ACCESS_DENIED') {
                logger.info('Unauthorized app access detected, redirecting to login');
                window.location.href = '/login';
            } else {
                logger.info('Session expired, redirecting to login');
                window.location.href = '/login';
            }
        }

        // Handle 403 Forbidden - account deactivated
        if (status === 403 && message.includes('deactivated') && !isOnLoginPage && !isPublicFormPage) {
            logger.info('Account deactivated, redirecting to login');
            // Clear any local state and redirect
            window.location.href = '/login?reason=deactivated';
        }

        // Log other errors
        if (status !== 401) {
            logger.error(`API Error: ${status || 'Network Error'}`);
        }

        return Promise.reject(error);
    }
);

// =============================================================================
// EXPORTS
// =============================================================================

export { apiClient, API_BASE_URL, API_URL };
export default apiClient;
